import Tooltip from "@material-ui/core/Tooltip";
import React from "react";
import { cx } from "../utils";
import {
  deviceAlert,
  deviceRunning,
  formatDeviceAlertText,
  formatDeviceAriaLabel,
  formatDeviceDetail,
  formatDeviceLastCheck,
  formatDeviceSources,
  normaliseDevices,
  NO_RECENT_CHECK,
} from "./deviceTagDisplay";
import "./CcDeviceTags.css";

// The catheter's own red-tag wording; any other red tag (a tube out or blocked)
// says its alert text as it is.
const isCatheterAlert = (device) =>
  device?.key === "catheter" ||
  deviceAlert(device)?.key === "catheter_not_draining";

const DeviceTooltip = ({ device, alertText }) => {
  const sources = formatDeviceSources(device);
  const lastCheck = formatDeviceLastCheck(device.last_check);
  const running = deviceRunning(device);
  return (
    <span className="cc-device-tag__tooltip">
      {alertText && isCatheterAlert(device) ? (
        <strong>
          Blocked: {alertText}. It stays red until someone records “Catheter
          draining again” or “Catheter changed”.
        </strong>
      ) : alertText ? (
        <strong>{alertText}</strong>
      ) : null}
      {running && !alertText ? <span>{running}</span> : null}
      {sources ? <span>From {sources}</span> : null}
      <span>Last check: {lastCheck || NO_RECENT_CHECK}</span>
    </span>
  );
};

/**
 * One device tag — icon, label, optional detail ("🧪 Catheter indwelling").
 * A standing care fact in one quiet blue style; the tooltip says where it
 * comes from (plan / register) and when it was last checked.
 *
 * The one exception: a catheter still not draining (tag.alert, tone red)
 * turns the tag red and says so — "🧪 Catheter · not draining since 06:10".
 * A tube out or blocked (tag.alert, level critical/high) does the same —
 * "PEG · Tube out 09:40" — and a running feed is quiet detail, not colour.
 */
export const CcDeviceTag = ({ device, className }) => {
  if (!device) return null;
  const alertText = formatDeviceAlertText(device);
  // Not red: the detail, and what is running ("Feed running since 20:02").
  const quietDetail = formatDeviceDetail(device);
  return (
    <Tooltip
      title={<DeviceTooltip device={device} alertText={alertText} />}
      arrow
      placement="top"
    >
      <span
        className={cx(
          "cc-device-tag",
          alertText && "cc-device-tag--alert",
          className,
        )}
        aria-label={formatDeviceAriaLabel(device)}
      >
        {device.icon ? (
          <span className="cc-device-tag__icon" aria-hidden>
            {device.icon}
          </span>
        ) : null}
        <span className="cc-device-tag__label">{device.label}</span>
        {alertText ? (
          <span className="cc-device-tag__alert">· {alertText}</span>
        ) : quietDetail ? (
          <small className="cc-device-tag__detail">{quietDetail}</small>
        ) : null}
      </span>
    </Tooltip>
  );
};

/**
 * The device line (card_summary.devices / overview.devices). Every tag always
 * shows — never truncated into "+N". Renders nothing when there are none.
 */
const CcDeviceTags = ({ devices, className }) => {
  const list = normaliseDevices(devices);
  if (list.length === 0) return null;
  return (
    <div className={cx("cc-device-tags", className)}>
      {list.map((device) => (
        <CcDeviceTag key={device.key} device={device} />
      ))}
    </div>
  );
};

export default CcDeviceTags;
